import * as React from "react";
import { cn } from "./cn";

type Tone = "info" | "success" | "warning" | "error";

// Tone accent = the left border colour, mapped to SEMANTIC tokens so every tone
// tracks per-portal theming: info→infoColor (--color-blue), success→successColor
// (--color-success), warning→warningColor, error→destructiveColor. (Previously
// success used --color-lime and error hardcoded #B00020 — both ignored the
// themeable colours.)
// N24 alert anatomy: rounded panel, full 1px border, tone-tinted surface —
// keeping the tone accent as the left edge so scanning stays easy. Tones stay
// on semantic tokens so every one tracks per-portal theming.
const tones: Record<Tone, string> = {
  info: "bg-blue/5 border-blue/30 border-l-4 border-l-blue text-foreground",
  success: "bg-success/10 border-success/30 border-l-4 border-l-success text-foreground",
  warning: "bg-warning/5 border-warning/30 border-l-4 border-l-warning text-foreground",
  error: "bg-destructive/5 border-destructive/30 border-l-4 border-l-destructive text-foreground",
};

// How a screen reader announces it follows the tone. `alert` is assertive — it
// interrupts whatever is being read — and every tone used it, so "Saved" cut in
// as hard as "Payment failed". Errors and warnings keep it; confirmations and
// information are `status` (polite: spoken when the reader is idle). A caller
// that knows better still overrides `role`.
const roles: Record<Tone, "alert" | "status"> = {
  info: "status",
  success: "status",
  warning: "alert",
  error: "alert",
};

export function Alert({
  tone = "info",
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { tone?: Tone }) {
  return (
    <div
      className={cn("p-4 text-sm rounded-lg border", tones[tone], className)}
      role={roles[tone]}
      {...props}
    >
      {children}
    </div>
  );
}
